/**
 * `<page-generation>` — one generation, routed at
 * `/instances/:id/generations/:gen`.
 *
 * Implements `SC-F600` (an immutable snapshot, with everything that identifies
 * it), `SC-F608` (**both** digests, side by side, because their equality is what
 * makes a publish true and showing only one hides the check), `SC-F611` (who,
 * when, why, by which path, and whether it landed — including the override reason,
 * which is the half a digest cannot supply), `SC-F610` (the diff back to this
 * generation, framed as rolling **forward** to an older desired state) and
 * `SC-F619` (the archive).
 *
 * Stories: `SC-S600`, `SC-S608`, `SC-S609`, `SC-S610`, `SC-S614`.
 *
 * On a `publish_failed` generation this page is the incident screen: the failure
 * reason verbatim, both digests so the mismatch is visible rather than merely
 * asserted, and the statement that the previous generation is still the one
 * serving queries.
 */

import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  publishClassVariant,
  toast,
  type BnBadgeVariant,
  type BnKvRow,
} from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import {
  bundleFilename,
  downloadGenerationBundle,
  getGeneration,
  listGenerations,
  restartClass,
  CHANGE_CLASS_LABELS,
  GENERATION_STATE_LABELS,
  type Generation,
} from "../api/generations.js";
import { getInstance, type Instance } from "../api/instances.js";
import { hrefFor, navigate } from "../nav.js";

const STATE_TONE: Record<string, BnBadgeVariant> = {
  draft: "neutral",
  validated: "info",
  publishing: "info",
  active: "success",
  publish_failed: "error",
  superseded: "neutral",
};

@customElement("page-generation")
export class PageGeneration extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";
  /** The generation number, from the route. It arrives as a string. */
  @property() generation = "";

  @state() private gen: Generation | null = null;
  /** The live generation, so a failed publish can name what is still serving. */
  @state() private live: Generation | null = null;
  @state() private instance: Instance | null = null;
  @state() private loading = true;
  @state() private failure: ApiError | null = null;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .wrap {
      max-width: 56rem;
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 22rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .badges {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-top: var(--bino-space-xs);
    }
    .head-actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .quote {
      margin: 0 0 var(--bino-space-md);
      padding-left: var(--bino-space-md);
      border-left: 3px solid var(--bino-border);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      max-width: 72ch;
    }
    .failed {
      padding: var(--bino-space-sm);
      border: 1px solid var(--bino-error-border);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-error-bg);
      color: var(--bino-text);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      white-space: pre-wrap;
      overflow-x: auto;
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
  `;

  /** Driven from `willUpdate` alone: the router sets both properties before the
   * first update, so loading from `connectedCallback` too would fetch twice. */
  override willUpdate(changed: PropertyValues<this>) {
    if ((changed.has("instanceId") || changed.has("generation")) && this.instanceId && this.generation) {
      void this.load();
    }
  }

  private get number(): number {
    return Number.parseInt(this.generation, 10);
  }

  private async load() {
    this.loading = true;
    try {
      const [gen, history, instance] = await Promise.all([
        getGeneration(this.instanceId, this.number),
        listGenerations(this.instanceId).catch(() => ({ items: [] as Generation[], next: null })),
        getInstance(this.instanceId).catch(() => null),
      ]);
      this.gen = gen;
      this.live = history.items.find((g) => g.state === "active") ?? null;
      this.instance = instance;
      this.failure = null;
    } catch (err) {
      this.failure = parseError(err);
      this.gen = null;
    } finally {
      this.loading = false;
    }
  }

  private async download() {
    const gen = this.gen;
    if (!gen) return;
    try {
      const blob = await downloadGenerationBundle(this.instanceId, gen.generation);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = bundleFilename(this.instance?.name ?? "instance", gen.generation);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      toast(parseError(err).message, "error");
    }
  }

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  /** The identity rows. The digests are monospace and copyable because they are
   * read character by character and compared by eye. */
  private identityRows(gen: Generation): BnKvRow[] {
    const rows: BnKvRow[] = [
      { label: "Generation", value: String(gen.generation) },
      { label: "State", value: GENERATION_STATE_LABELS[gen.state] ?? gen.state },
      { label: "Forked from", value: gen.parent === null ? "nothing — the first" : `generation ${gen.parent}` },
      { label: "Created", value: when(gen.created_at) },
      { label: "Created by", value: gen.created_by_label || gen.created_by || "system" },
    ];
    if (gen.published_at) rows.push({ label: "Published", value: when(gen.published_at) });
    if (gen.change_class) {
      rows.push({
        label: "Change class",
        value: `${CHANGE_CLASS_LABELS[gen.change_class] ?? gen.change_class} — ${
          restartClass(gen.change_class) ? "restart-class" : "no restart"
        }`,
      });
    }
    if (gen.publish_path) rows.push({ label: "Publish path", value: gen.publish_path });
    if (gen.rendered_bytes) rows.push({ label: "Rendered size", value: `${gen.rendered_bytes} bytes` });
    if (gen.sluice_version) rows.push({ label: "Sluice version", value: gen.sluice_version });
    if (gen.image_digest) rows.push({ label: "Image digest", value: gen.image_digest });
    return rows;
  }

  /**
   * The digest pair (`SC-F608`). Both are shown always — on success so the reader
   * can see the check happened, on failure so the mismatch is visible rather than
   * summarised as the word "failed".
   */
  private renderDigests(gen: Generation) {
    const rendered = gen.rendered_digest ?? "";
    const confirmed = gen.confirmed_digest ?? "";
    const matched = rendered !== "" && rendered === confirmed;
    return html`<bn-card heading="Digest read-back">
      <p class="copy">
        A publish is recorded as successful only after the control plane reads
        <code>config_digest</code> back from the instance's own <code>/admin/healthz</code> and finds
        it equal to the digest it computed at render time. That equality is the whole definition:
        "we sent it" is not "it is running".
      </p>
      <bn-kv
        .rows=${[
          { label: "Rendered by the control plane", value: rendered || undefined },
          { label: "Reported by the instance", value: confirmed || undefined },
        ] as BnKvRow[]}
        unsetText="not reported"
      ></bn-kv>
      <p class="copy">
        ${matched
          ? html`<strong>The instance confirmed this generation.</strong> The two values are
              identical.`
          : confirmed
            ? html`<strong>The digests differ.</strong> This generation was never live; whatever was
                running stayed running.`
            : html`<strong>The instance never reported a digest.</strong> Either the read timed out or
                the change never reached it — in both cases the previous generation kept serving.`}
      </p>
    </bn-card>`;
  }

  /** The failure panel. It quotes the reason verbatim and names what is still
   * live, because those two facts are what an operator needs at 03:00. */
  private renderFailure(gen: Generation) {
    if (gen.state !== "publish_failed") return nothing;
    return html`<bn-card heading="This publish failed">
      <p class="copy">
        <strong>
          ${this.live
            ? `Generation ${this.live.generation} is still live and still serving queries.`
            : "Nothing became live; the instance is running whatever it was running before."}
        </strong>
        A failed publish is terminal for its own generation: the fix is a new draft, so this attempt
        stays in the history rather than being tidied away by the retry.
      </p>
      ${gen.failure_reason
        ? html`<p class="failed">${gen.failure_reason}</p>`
        : html`<p class="copy">The store recorded no reason for this failure.</p>`}
      <p class="copy" style="margin-top: var(--bino-space-md)">
        A retry re-renders from the structured model rather than reusing any partially applied
        artifact, and it must carry the digest you have just looked at — which is the step that puts
        this failure in front of a human.
      </p>
      <bn-button
        variant="primary"
        @click=${() => navigate(hrefFor("instances", this.instanceId, "publish"))}
      >
        Review the draft
      </bn-button>
    </bn-card>`;
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner block size="28" label="Loading the generation"></bn-spinner>`;
    }
    if (this.failure) {
      return html`<a class="back" href=${hrefFor("instances", this.instanceId, "generations")}
          >← Generations</a
        >
        <bn-card heading="Could not load this generation">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            <div>
              <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
            </div>
          </div>
        </bn-card>`;
    }
    const gen = this.gen;
    if (!gen) return nothing;
    const from = gen.parent === null ? "live" : gen.parent;
    return html`<div class="wrap">
      <a class="back" href=${hrefFor("instances", this.instanceId, "generations")}>← Generations</a>
      <header>
        <div class="head-text">
          <h1>Generation ${gen.generation}</h1>
          <div class="badges">
            <bn-badge .variant=${STATE_TONE[gen.state] ?? "neutral"}>
              ${GENERATION_STATE_LABELS[gen.state] ?? gen.state}
            </bn-badge>
            ${gen.change_class
              ? html`<bn-badge .variant=${publishClassVariant(gen.publish_path ?? gen.change_class)}>
                  ${restartClass(gen.change_class) ? "restart" : "reload"}
                </bn-badge>`
              : nothing}
            ${gen.rollback_blocked_reason
              ? html`<bn-badge variant="warning">rollback blocked</bn-badge>`
              : nothing}
          </div>
        </div>
        <div class="head-actions">
          <bn-button
            @click=${() =>
              navigate(hrefFor("instances", this.instanceId, "generations", gen.generation, "yaml"))}
          >
            Rendered YAML
          </bn-button>
          <bn-button
            @click=${() =>
              navigate(
                `${hrefFor("instances", this.instanceId, "diff")}?from=${from}&to=${gen.generation}`,
              )}
          >
            Diff
          </bn-button>
          <bn-button @click=${() => void this.download()}>Download bundle</bn-button>
        </div>
      </header>

      <div class="stack">
        ${gen.message ? html`<p class="quote">“${gen.message}”</p>` : nothing}
        ${this.renderFailure(gen)}
        <bn-card heading="Identity">
          <bn-kv .rows=${this.identityRows(gen)}></bn-kv>
        </bn-card>
        ${this.renderDigests(gen)}
        ${gen.override_reason
          ? html`<bn-card heading="This publish proceeded past a block">
              <p class="copy">
                An owner overrode a blocking finding and recorded why. The reason is required and is
                never defaulted: an empty value is the statement that no override was exercised, so a
                column that is always full would be worth nothing to an auditor.
              </p>
              <p class="quote">“${gen.override_reason}”</p>
            </bn-card>`
          : nothing}
        ${gen.rollback_blocked_reason
          ? html`<bn-panel eyebrow="Rolling forward to this generation is blocked">
              <p class="copy">${gen.rollback_blocked_reason}</p>
            </bn-panel>`
          : gen.state === "superseded" || gen.state === "publish_failed"
            ? html`<bn-panel eyebrow="Restoring this content">
                <p class="copy">
                  Publishing this content again does not rewind anything: it creates a
                  <strong>new</strong> generation whose content equals this one. Kubernetes keeps no
                  previous Pod spec, so rolling backwards is not a primitive — rolling forward to an
                  older desired state is what makes any target reachable.
                </p>
                <bn-button
                  @click=${() => navigate(hrefFor("instances", this.instanceId, "generations"))}
                >
                  Roll forward from the history
                </bn-button>
              </bn-panel>`
            : nothing}
      </div>
    </div>`;
  }
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-generation": PageGeneration;
  }
}
